import { Form } from "antd";
import { Tabs } from "antd-v5";
import { TabsProps } from "antd-v5/lib";
import BaseModal from "components/BaseModal";
import message from "components/CustomMessage";
import { MODAL_SIZE_MEDIUM } from "constants/Layout";
import { PermissionSetMap } from "constants/RBACConstants";
import _, { isEmpty } from "lodash";
import React, {
  ForwardedRef,
  MutableRefObject,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { hasPermission } from "utils/authService";
import { FieldTypes, generateFormFields } from "utils/formUtils";
import ModalUtil from "utils/modalUtil";

import JiraMappingForm from "../jira/JiraMappingForm";
import {
  AUTHORIZE_TYPE,
  CONFIG_TYPE_TAB,
  JiraIntegrationConfigLoginFormPropsType,
  JiraLoginConfigType,
  RADIO_OPTION_TAB_MAP,
  USER_CONFIG_TYPE,
} from "./JiraIntegrationConstants";
import {
  advancedConfigurationField,
  getAuthTypeFieldMap,
  getNameAndUrlFields,
} from "./JiraIntegrationUtils";

function JiraIntegrationLoginConfigForm(
  {
    name,
    loginConfigList,
    handleMapping,
    ...props
  }: JiraIntegrationConfigLoginFormPropsType,
  ref: ForwardedRef<any>,
) {
  const isEdit = !_.isNil(props.config);
  const [form] = Form.useForm();
  const [auth, setAuth] = useState(() =>
    isEdit ? jiraLoginType(props.config) : USER_CONFIG_TYPE.REGULAR,
  );
  const [selectedConfigTypeTab, setSelectedConfigTypeTab] = useState<string>(
    () => getJiraConfigTypeTab(props.config),
  );
  const [apiToken, setApiToken] = useState("");
  const thisRef = useRef<MutableRefObject<any>>();

  useImperativeHandle(ref, () => ({
    ...thisRef,
  }));

  const handleTabChange = useCallback((tab) => {
    setSelectedConfigTypeTab(tab);
  }, []);

  const onRadioChange = useCallback(
    (e) => {
      setAuth(e.target.value);
      form.setFieldsValue({
        version: [
          AUTHORIZE_TYPE.BASIC,
          AUTHORIZE_TYPE.OAUTH,
          AUTHORIZE_TYPE.PERSONAL_ACCESS_TOKEN,
        ].includes(e.target.value)
          ? "V2"
          : "V3",
      });
    },
    [form],
  );

  const getVerificationCode = useCallback(async () => {
    await form.validateFields(["url", "consumerKey", "privateKey", "hostUrl"]);
    const { url, consumerKey, privateKey, hostUrl } = form.getFieldsValue();
    const { authorizationURL, requestToken, redirectURL } =
      await ToolsIntegrationsAPI.getJiraAuthURL({
        url,
        consumerKey,
        privateKey,
        hostUrl,
      }).promise;
    setApiToken(requestToken);
    window.open(redirectURL || authorizationURL, "_blank", "rel=noopener");
  }, [form]);

  const jiraLabel = (
    <>
      <span>
        You can integrate Jira using one of the following options: Basic Auth,
        Token Auth, OAuth or via Personal Access Token. <br />
        <strong>For OAuth (Jira On-Prem):</strong> Enter consumer and private
        key and click on “Allow Access”. A separate tab will be opened where
        user needs to click on “Allow”, post which a verification code will be
        generated. Paste the code in the config popup, click on Save.
      </span>
    </>
  );

  const configTypeRadioSelections = useMemo(
    () => ({
      name: "loginType",
      label: "",
      type: FieldTypes.RADIO,
      options: RADIO_OPTION_TAB_MAP[selectedConfigTypeTab],
      props: {
        onChange: onRadioChange,
      },
    }),
    [onRadioChange, selectedConfigTypeTab],
  );

  const AUTH_TYPE_FIELD_MAP = useMemo(
    () =>
      getAuthTypeFieldMap({
        isRequired: isEdit ? false : true,
        getVerificationCode,
      }),
    [isEdit, getVerificationCode],
  );

  const cloudTabFields = [
    configTypeRadioSelections,
    ...getNameAndUrlFields({ loginConfigList }),
    ...AUTH_TYPE_FIELD_MAP[auth!],
  ];

  const dataCenterTabFields = [
    configTypeRadioSelections,
    {
      type: "Label",
      value: jiraLabel,
    },
    ...getNameAndUrlFields({ loginConfigList }),
    ...AUTH_TYPE_FIELD_MAP[auth!],
    advancedConfigurationField,
  ];

  const onOk = async () => {
    await form.validateFields();
    try {
      const toolName = "JIRA";
      let payload = form.getFieldsValue();
      if (auth === AUTHORIZE_TYPE.OAUTH) {
        payload = {
          ...payload,
          apiToken,
          tokenType: "ACCESS_TOKEN",
          userType: "ON_PREM",
        };
      }
      if (auth === AUTHORIZE_TYPE.PERSONAL_ACCESS_TOKEN) {
        payload = {
          ...payload,
          loginType: "BASIC",
          tokenType: "PERSONAL_ACCESS_TOKEN",
          userType: "ON_PREM",
        };
      }
      if (auth === AUTHORIZE_TYPE.BASIC) {
        payload = {
          ...payload,
          userType: "ON_PREM",
        };
      }

      if (Object.values(USER_CONFIG_TYPE).includes(auth!)) {
        payload = {
          ...payload,
          userType: USER_CONFIG_TYPE[auth! as keyof typeof USER_CONFIG_TYPE],
          loginType: "TOKEN",
        };
      }

      const response = await ToolsIntegrationsAPI[
        !isEdit
          ? "postTicketSystemToolLoginDetails"
          : "updateTicketSystemToolLoginDetails"
      ](toolName, payload, props.config?.id).promise;

      message.success(`Jira Login Configuration created`);
      if (!isEdit) {
        if (_.isFunction(handleMapping)) {
          handleMapping(response);
        } else {
          ModalUtil.show({
            content: (
              <JiraMappingForm
                onUpdateConfigComponent={props.onUpdateConfigComponent}
                name={name}
                loginConfig={response}
                loginConfigId={response.id}
                {...props}
              />
            ),
          });
        }
      }
      props.onUpdateConfigComponent && props.onUpdateConfigComponent(true);
    } catch (err) {
      console.log(err);
      // TS resolve: err is of type unkown
      // @ts-expect-error
      if (!_.isEmpty(err.response?.data?.errors)) {
        // TS resolve: err is of type unkown
        // @ts-expect-error
        const { field, defaultMessage } = err.response.data.errors[0];
        form.setFields([
          {
            name: field,
            errors: [defaultMessage],
          },
        ]);
      }
      // TS resolve: err is of type unkown
      // @ts-expect-error
      return Promise.reject(err.response?.data?.message);
    }
  };

  const modalProps = {
    title: (
      <>
        {isEdit ? "Edit" : "Add"} {name} Login Credentials{" "}
      </>
    ),
    width: MODAL_SIZE_MEDIUM,
    disabled: !hasPermission(
      isEdit ? PermissionSetMap.EDIT_TOOL_CONFIG : PermissionSetMap.WRITE_TOOLS,
    ),
    onOk: onOk,
  };

  const tabItems: TabsProps["items"] = [
    {
      key: CONFIG_TYPE_TAB.JIRA_CLOUD,
      label: "Cloud",
      children: generateFormFields(cloudTabFields),
    },
    {
      key: CONFIG_TYPE_TAB.JIRA_DATA_CENTER,
      label: "Data Center",
      children: generateFormFields(dataCenterTabFields),
    },
  ];
  return (
    <BaseModal
      modalProps={
        _.isNull(apiToken) && auth === AUTHORIZE_TYPE.OAUTH
          ? { ...modalProps, disabled: true }
          : modalProps
      }
      ref={thisRef as MutableRefObject<any>}
      afterClose={props.afterClose}
    >
      <Form
        initialValues={
          isEdit
            ? {
                ...props.config,
                loginType: jiraLoginType(props.config),
              }
            : {
                loginType: auth,
                version: [
                  AUTHORIZE_TYPE.BASIC,
                  AUTHORIZE_TYPE.OAUTH,
                  AUTHORIZE_TYPE.PERSONAL_ACCESS_TOKEN,
                ].includes(auth!)
                  ? "V2"
                  : "V3",
              }
        }
        form={form}
        layout="vertical"
      >
        <Tabs
          defaultActiveKey={selectedConfigTypeTab}
          onChange={handleTabChange}
          items={tabItems}
        />
      </Form>
    </BaseModal>
  );
}

export default forwardRef(JiraIntegrationLoginConfigForm);

const jiraLoginType = (config: JiraLoginConfigType) => {
  const { loginType, tokenType, userType } = config || {};
  if (tokenType === "ACCESS_TOKEN") {
    return AUTHORIZE_TYPE.OAUTH;
  } else if (tokenType === "PERSONAL_ACCESS_TOKEN") {
    return AUTHORIZE_TYPE.PERSONAL_ACCESS_TOKEN;
  } else if (userType) {
    return userType === "ON_PREM" && loginType === "BASIC"
      ? AUTHORIZE_TYPE.BASIC
      : USER_CONFIG_TYPE[userType as keyof typeof USER_CONFIG_TYPE];
  }
};

const getJiraConfigTypeTab = (config: JiraLoginConfigType) => {
  if (!isEmpty(config)) {
    switch (config.userType) {
      case "SERVICE_ACCOUNT":
      case "REGULAR":
        return CONFIG_TYPE_TAB.JIRA_CLOUD;
      case "ON_PREM":
        return CONFIG_TYPE_TAB.JIRA_DATA_CENTER;
      default:
        return CONFIG_TYPE_TAB.JIRA_CLOUD;
    }
  }
  return CONFIG_TYPE_TAB.JIRA_CLOUD;
};
